import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Button, DefinitionList, EmptyState, Grid, Inline, Link, PageHeader, Panel, Skeleton, Stack, t, Text, toast,
} from '@kanbios/ui';
import type { ReactNode } from 'react';
import type { fleetState, maintenance } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { OutsideScopeBanner, useDisplayScope } from '../../components/ScopePicker';
import { RefusalPage } from '../../components/Refusal';
import { EntryLinks, EnvironmentBadge, securityRowLabel, securityRowTone, ServerStateBadge, serverStateColumn } from '../../components/ServerState';
import { ListTable } from '../../components/ListTable';
import { DeploymentStatus } from '../../components/DeploymentStatus';
import { EnvironmentBadges, ObligationBadges } from '../../components/Inventory';
import { AddressTable } from '../../components/domains/AddressTable';
import { PullRequestNotice } from '../../components/catalogue/PullRequestNotice';
import { QualificationDialog } from '../../components/catalogue/QualificationDialog';
import { ProjectTeam } from '../../components/catalogue/ProjectTeam';
import { api, unwrap } from '../../lib/api';
import { shortDate, shortVersion } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useEnvironmentName } from '../../lib/environments';

type Project = maintenance.components['schemas']['ProjectDetail'];
type ProjectDeployment = maintenance.components['schemas']['ProjectDeployment'];
type ProjectServer = maintenance.components['schemas']['ProjectServer'];
type Matrix = fleetState.components['schemas']['ProjectMatrix'];
type Row = Matrix['rows'][number];
/**
 * Project page (SPEC-005), DSM « Record with tabs » (LayoutRecordBand): the
 * band says which project and which section. D113: the overview says what needs an action (left) beside the
 * project's summary (right), like the application's (D111); the matrix applications × environments is the
 * « Applications » tab: D117, one row per deployment in a DataTable (state, application, environment,
 * status, version, servers, address); a row opens its application on that environment.
 */
export function ProjectPage() {
  const envName = useEnvironmentName();
  const { project = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { enums } = useSession();
  const scope = useDisplayScope();
  const highlighted = params.get('application');
  // RG-005-4: an application to highlight opens the matrix, the « Applications » tab since D113.
  const tab = params.get('tab') ?? (highlighted ? 'applications' : 'overview');
  const [qualifying, setQualifying] = useState(false);

  const detail = useQuery({
    queryKey: ['project', project, scope],
    queryFn: async () => unwrap(await api.maintenance.GET('/projects/{project}', { params: { path: { project }, query: { scope } } })),
    refetchInterval: 30_000,
    retry: false,
  });
  const matrix = useQuery({
    queryKey: ['project-matrix', project],
    queryFn: async () => unwrap(await api.fleetState.GET('/projects/{project}/matrix', { params: { path: { project } } })),
    enabled: detail.isSuccess,
    refetchInterval: 30_000,
  });

  // RG-005-4 and its refusal: an application of another project shows the whole matrix, said once.
  useEffect(() => {
    if (highlighted && matrix.data && !matrix.data.rows.some(r => r.application === highlighted)) {
      toast({ title: t('project.applicationElsewhere'), tone: 'warn' });
      const next = new URLSearchParams(params);
      next.delete('application');
      setParams(next, { replace: true });
    }
  }, [highlighted, matrix.data]);

  // SPEC-034 § 9: the deployments the platform installed — their version is the one it deployed, even before the
  // agent reports it; each one has its own page.
  const installed = useQuery({
    queryKey: ['environments', project],
    queryFn: async () => unwrap(await api.maintenance.GET('/environments', { params: { query: { project } } })),
    enabled: detail.isSuccess,
    retry: false,
  });
  const installedOf = (deployment: string) => installed.data?.items.find(x => x.deployment === deployment);

  // SPEC-034 § 12.4: a retired deployment (volumes kept, no server) says its installation state, not « Inconnu ».
  const RETIRED = ['retire_requested', 'retiring', 'unlinking', 'retired', 'purging', 'resetting', 'purged'];
  const retiredOf = (deployment: string) => { const i = installedOf(deployment); return i && RETIRED.includes(i.state) ? i.state : null; };
  // A link of before D117 (?deployment=) opens the application of the deployment it named, on its environment.
  const legacy = params.get('deployment');
  useEffect(() => {
    const row = legacy ? matrix.data?.rows.find(r => Object.values(r.cells).some(c => c?.deployment === legacy)) : undefined;
    const env = row ? Object.entries(row.cells).find(([, c]) => c?.deployment === legacy)?.[0] : undefined;
    if (row && env) navigate(`/applications/${encodeURIComponent(row.application)}?env=${encodeURIComponent(env)}`);
  }, [legacy, matrix.data]);

  // The team's rights (the same query as its tab: one request).
  const team = useQuery({
    queryKey: ['project-team', project],
    queryFn: async () => unwrap(await api.maintenance.GET('/projects/{project}/team', { params: { path: { project } } })),
    enabled: tab === 'team',
  });
  const [editingTeam, setEditingTeam] = useState(false);
  const crumbs = [{ label: t('nav.projects'), href: '/projects' }, { label: detail.data?.title ?? project }];
  if (detail.error) return <Shell crumbs={crumbs}><RefusalPage error={detail.error} /></Shell>;
  const p = detail.data;
  const label = (list: { value: string; label: string }[], value: string) => list.find(i => i.value === value)?.label ?? value;
  const canManage = p?.actions.includes('project.servers.update') ?? false;

  const header = (
    <PageHeader
      band
      eyebrow={t('page.kind.project')}
      title={p?.title ?? project}
      status={p ? (p.state ? <ServerStateBadge state={p.state} /> : undefined) : undefined}
      subtitle={p ? [p.client.title, t('project.team', { team: p.team }), t('project.criticality', { criticality: label(enums.criticalities, p.criticality) }), ...p.obligations.map(o => label(enums.obligations, o))].join(' · ') : undefined}
      tabs={{
        label: t('project.tabs.label'),
        value: tab,
        onChange: id => setParams(id === 'overview' ? {} : { tab: id }),
        items: (p?.tabs ?? ['overview', 'applications', 'servers', 'settings']).map(id => ({ id, label: t(`project.tabs.${id}`) })),
        // SPEC-005, DSM Tabs: the action of the active tab sits at the end of the tab bar, and follows the tab.
        action: (tab === 'overview' || tab === 'applications' || tab === 'servers') && canManage
          ? <Button size="sm" icon="dns" onClick={() => navigate(`/projects/${encodeURIComponent(project)}/servers`)}>{t('project.manageServers')}</Button>
          : tab === 'team' && team.data?.canEdit
            ? <Button size="sm" icon="group" onClick={() => setEditingTeam(true)}>{t('team.edit')}</Button>
            : undefined,
      }}
    />
  );

  return (
    <Shell crumbs={crumbs} header={header}>
      {!p ? (
        <Skeleton lines={8} />
      ) : (
        <Stack gap={24}>
          <OutsideScopeBanner kind="project" outside={p.outsideDisplayScope} />
          {tab === 'overview' && <ProjectOverview p={p} rows={matrix.data?.rows ?? []} environments={matrix.data?.environments ?? []} installed={installed.data?.items ?? []} loading={matrix.isPending} onApplications={() => setParams({ tab: 'applications' })} />}
          {tab === 'applications' && (
            <Panel title={t('project.applications.title')} subtitle={t('project.applications.subtitle')} flush>
              {/* D120: the server searches, filters, sorts and pages; the installed deployments add their status. */}
              <ListTable<ProjectDeployment>
                label={t('project.applications.title')}
                queryKey={['project-deployments', project]}
                fetch={async query => unwrap(await api.maintenance.GET('/projects/{project}/deployments', { params: { path: { project }, query: query as never } }))}
                rowKey="name"
                defaultSort={{ key: 'application', dir: 'asc' }}
                search={t('project.applications.search')}
                facets={[
                  { key: 'state', label: t('list.facets.state'), valueLabel: v => enums.states.find(x => x.value === v)?.label ?? v },
                  { key: 'environment', label: t('project.applications.environment'), valueLabel: envName },
                  { key: 'application', label: t('project.matrix.application') },
                  { key: 'role', label: t('project.matrix.role'), valueLabel: v => label(enums.applicationRoles, v) },
                ]}
                // RG-005-4: the application asked for, its first deployment.
                activeRow={highlighted ? r => r.application === highlighted : undefined}
                onRowClick={r => navigate(`/applications/${encodeURIComponent(r.application)}?env=${encodeURIComponent(r.environment)}`)}
                rowTone={r => securityRowTone(r.security)}
                rowToneLabel={r => securityRowLabel(r.security)}
                columns={[
                  // The state first, as in every list (SPEC-005).
                  serverStateColumn<ProjectDeployment>(enums, r => ({ state: r.state, retired: retiredOf(r.name) }), { securityRow: true }),
                  { key: 'application', label: t('project.matrix.application'), strong: true, pinned: true, grow: true, width: 280, accessor: (r: ProjectDeployment) => r.application },
                  { key: 'environment', label: t('project.applications.environment'), width: 150, accessor: (r: ProjectDeployment) => envName(r.environment),
                    render: (r: ProjectDeployment) => <EnvironmentBadge environment={r.environment} type={r.environmentType} /> },
                  { key: 'role', label: t('project.matrix.role'), width: 140, accessor: (r: ProjectDeployment) => label(enums.applicationRoles, r.role) },
                  // D96: what runs against what was declared, for the deployments the platform installed.
                  { key: 'status', label: t('applications.columns.status'), width: 150, sortable: false,
                    render: (r: ProjectDeployment) => { const i = installedOf(r.name); return i ? <DeploymentStatus state={i.state} observed={i.observed} size="sm" /> : <Text variant="body-sm" tone="mute">—</Text>; } },
                  { key: 'version', label: t('project.deployment.version'), width: 130, mono: true, sortable: false,
                    accessor: (r: ProjectDeployment) => { const i = installedOf(r.name); return retiredOf(r.name) || !i?.release ? '—' : shortVersion(i.release); } },
                  { key: 'servers', label: t('project.applications.servers'), width: 220, mono: true, sortable: false, accessor: (r: ProjectDeployment) => r.servers.map(x => x.name).join(', ') || '—' },
                  // D109: its entry points when the repository declares them (« Back-office » → /admin), else its address.
                  { key: 'url', label: t('applications.columns.url'), width: 200, sortable: false,
                    accessor: (r: ProjectDeployment) => installedOf(r.name)?.entryPoints.map(e => e.url).join(' ') || (r.url ?? ''),
                    render: (r: ProjectDeployment) => <EntryLinks url={r.url} entryPoints={installedOf(r.name)?.entryPoints} /> },
                ]}
                empty={<EmptyState framed={false} icon="apps" title={t('project.matrix.empty')} />}
              />
            </Panel>
          )}
          {tab === 'servers' && (
            // « Gérer les serveurs » is the tab bar's action, on every tab.
            <Panel title={t('project.servers.title')} subtitle={t('project.servers.subtitle')} flush>
              <ListTable<ProjectServer>
                label={t('project.servers.title')}
                queryKey={['project-servers', project]}
                fetch={async query => unwrap(await api.maintenance.GET('/projects/{project}/servers', { params: { path: { project }, query: query as never } }))}
                rowKey="name"
                defaultSort={{ key: 'state', dir: 'asc' }}
                search={t('project.servers.search')}
                facets={[
                  { key: 'state', label: t('list.facets.state'), valueLabel: v => enums.states.find(x => x.value === v)?.label ?? v },
                  { key: 'environment', label: t('project.servers.environment'), valueLabel: v => enums.environments.find(e => e.value === v)?.label ?? v },
                  { key: 'placement', label: t('project.servers.placement'), valueLabel: v => t(`list.placement.${v}`) },
                ]}
                onRowClick={s => navigate(`/servers/${encodeURIComponent(s.name)}`)}
                columns={[
                  serverStateColumn<ProjectServer>(enums, s => ({ state: s.state })),
                  { key: 'name', label: t('project.servers.name'), mono: true, width: 220 },
                  { key: 'environment', label: t('project.servers.environment'), width: 140, render: (s: ProjectServer) => (s.environment ? <EnvironmentBadge environment={s.environment} /> : t('inventory.none')) },
                  { key: 'applications', label: t('project.servers.applications'), grow: true, sortable: false, render: (s: ProjectServer) => s.applications.join(', ') },
                  { key: 'clients', label: t('project.servers.placement'), width: 180, sortable: false, render: (s: ProjectServer) => (s.clients >= 2 ? t('project.servers.shared', { n: s.clients }) : t('project.servers.dedicated')) },
                ]}
                empty={<EmptyState framed={false} icon="dns" title={t('project.servers.empty')} />}
              />
            </Panel>
          )}
          {tab === 'domains' && (
            // D27: the project's addresses, and the ones to attach on the servers it shares.
            <Panel title={t('project.domains.title')} subtitle={t('project.domains.subtitle')}>
              <AddressTable project={p.name} />
            </Panel>
          )}
          {tab === 'team' && <ProjectTeam project={{ name: p.name, title: p.title }} editing={editingTeam} onEditClose={() => setEditingTeam(false)} />}
          {tab === 'settings' && <PullRequestNotice pullRequest={p.pullRequests.qualification} />}
          {tab === 'settings' && (
            <Panel
              title={t('project.settings.title')}
              actions={p.actions.includes('project.qualification.update')
                ? <Button variant="secondary" size="sm" onClick={() => setQualifying(true)}>{t('project.settings.editQualification')}</Button>
                : undefined}
            >
              <DefinitionList
                items={[
                  { label: t('project.settings.client'), value: p.client.title },
                  { label: t('project.settings.team'), value: p.team },
                  { label: t('project.settings.criticality'), value: label(enums.criticalities, p.criticality) },
                  { label: t('project.settings.qualification'), value: <ObligationBadges obligations={p.obligations} />, hint: p.qualification?.qualifiedAt ? t('project.settings.qualifiedAt', { date: shortDate(p.qualification.qualifiedAt) }) : t('project.settings.notQualified') },
                  { label: t('project.settings.environments'), value: <EnvironmentBadges environments={p.environments} /> },
                  { label: t('project.settings.repositories'), value: p.repositories.join(', '), mono: true },
                ]}
              />
            </Panel>
          )}
        </Stack>
      )}
      {p && qualifying && (
        <QualificationDialog
          open
          onClose={() => setQualifying(false)}
          project={{ name: p.name, title: p.title }}
          current={{ criticality: p.criticality, healthData: p.qualification?.healthData ?? null, dora: p.qualification?.dora ?? null, sensitivePersonalData: p.qualification?.sensitivePersonalData ?? null, internetExposed: p.qualification?.internetExposed ?? null }}
        />
      )}
    </Shell>
  );
}

/** The worst state first (SPEC-008). */
const STATE_ORDER = ['incident', 'unknown', 'to_update', 'maintenance', 'ok'];

type Installed = maintenance.operations['listEnvironments']['responses']['200']['content']['application/json']['items'][number];

/**
 * D113: the project's overview, as the application's (D111): what needs an action, each with the way to the
 * screen that treats it, or « Tout est en ordre »; its summary beside.
 */
function ProjectOverview({ p, rows, environments, installed, loading, onApplications }: { p: Project; rows: Row[]; environments: { name: string; type: string }[]; installed: Installed[]; loading: boolean; onApplications: () => void }) {
  const navigate = useNavigate();
  const { enums } = useSession();
  const envName = useEnvironmentName();
  const label = (list: { value: string; label: string }[], value: string) => list.find(i => i.value === value)?.label ?? value;
  // The latest installation of each deployment.
  const byDeployment = new Map<string, Installed>();
  for (const i of installed) byDeployment.set(i.deployment, i);
  const retired = (state: string | undefined) => Boolean(state && ['retire_requested', 'retiring', 'unlinking', 'retired', 'purging', 'resetting', 'purged'].includes(state));
  const openApplication = (application: string, env: string) => navigate(`/applications/${encodeURIComponent(application)}?env=${encodeURIComponent(env)}`);
  const openEnvironment = (deployment: string) => navigate(`/environments/${encodeURIComponent(deployment)}`);
  const items: ReactNode[] = [];
  let deployments = 0;
  for (const row of rows) {
    for (const [env, cell] of Object.entries(row.cells)) {
      if (!cell) continue;
      const i = byDeployment.get(cell.deployment);
      if (retired(i?.state)) continue;
      deployments++;
      const where = { application: row.application, environment: envName(env) };
      if (i?.state === 'failed') {
        items.push(<Alert key={`failed:${cell.deployment}`} tone="neg" action={t('application.summary.openEnvironment')} onAction={() => openEnvironment(cell.deployment)} title={t('project.summary.failed', where)} />);
      } else if (i?.observed === 'degraded' || i?.observed === 'gap') {
        items.push(<Alert key={`observed:${cell.deployment}`} tone={i.observed === 'degraded' ? 'neg' : 'warn'} action={t('application.summary.openEnvironment')} onAction={() => openEnvironment(cell.deployment)} title={t(`project.summary.${i.observed}`, where)} />);
      }
      if (cell.state === 'incident' || cell.state === 'to_update') {
        items.push(<Alert key={`state:${cell.deployment}`} tone={cell.state === 'incident' ? 'neg' : 'warn'} action={t('project.summary.openApplication')} onAction={() => openApplication(row.application, env)}
          title={t(`project.summary.state.${cell.state}`, where)} />);
      }
    }
  }
  // D119: one card per environment, its worst state and how many deployments, production last (the matrix's order).
  const cards = environments.map(({ name, type }) => {
    const cells = rows.flatMap(r => (r.cells[name] && !retired(byDeployment.get(r.cells[name]!.deployment)?.state) ? [r.cells[name]!] : []));
    const worst = STATE_ORDER.find(s => cells.some(c => c.state === s)) ?? null;
    return { name, type, worst, n: cells.length };
  });
  return (
    <Stack gap={24}>
    {cards.length > 0 && (
      <Grid min={220}>
        {cards.map(c => {
          const state = c.worst ? enums.states.find(x => x.value === c.worst) : undefined;
          return (
            <Panel key={c.name} title={envName(c.name)} subtitle={t('project.summary.deploymentsIn', { n: c.n })}>
              <Inline gap={8} align="center">
                <EnvironmentBadge environment={c.name} type={c.type} />
                {state ? <Badge tone={state.tone}>{state.label}</Badge> : <Text variant="body-sm" tone="mute">{t('inventory.noState')}</Text>}
              </Inline>
            </Panel>
          );
        })}
      </Grid>
    )}
    <Grid columns="2-1">
      <Panel title={t('application.summary.title')} subtitle={t('project.summary.subtitle', { n: deployments })}>
        {loading ? <Skeleton lines={3} /> : items.length
          ? <Stack gap={12}>{items}</Stack>
          : <Alert tone="pos" title={t('application.summary.allGood')} action={t('project.summary.openApplications')} onAction={onApplications}>{t('project.summary.allGoodBody', { n: rows.length, deployments })}</Alert>}
      </Panel>
      <Panel title={t('application.properties')}>
        <DefinitionList
          stacked
          items={[
            { label: t('project.settings.client'), value: <Link href={`/clients/${encodeURIComponent(p.client.name)}`} onClick={(e: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean }) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(`/clients/${encodeURIComponent(p.client.name)}`); }}>{p.client.title}</Link> },
            { label: t('project.settings.team'), value: p.team },
            { label: t('project.settings.criticality'), value: label(enums.criticalities, p.criticality) },
            { label: t('project.settings.qualification'), value: p.obligations.length ? <ObligationBadges obligations={p.obligations} /> : null },
            { label: t('project.settings.environments'), value: <EnvironmentBadges environments={p.environments} /> },
            { label: t('project.summary.applications'), value: String(rows.length) },
            { label: t('project.summary.servers'), value: String(p.servers.length) },
            { label: t('project.settings.repositories'), value: p.repositories.length ? p.repositories.join(', ') : null, mono: true },
          ]}
        />
      </Panel>
    </Grid>
    </Stack>
  );
}
